import {
  useRef,
  memo,
  useState,
  useCallback,
  useReducer,
  useEffect
} from 'react';
import Dropzone, { DropFilesEventHandler } from 'react-dropzone';
import { WidgetProps, IdSchema, UiSchema } from '@rjsf/utils';
import filesize from 'filesize';
import shortid from 'shortid';
import { CloudUpload } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';
import { Button } from '@/shadcn/button';
import { Switch } from '@/shadcn/switch';
import { Label } from '@/shadcn/label';

import { getFileExtension } from '@/components/fileIcon/file.utils';
import { isLockedWidget, FormLinks } from '@/components/forms/utils/links';
import { FieldTemplate } from '@/components/forms/components/formTemplates';

import { File } from './file/file.fileUpload';
import { Label as FieldLabel } from './label/label.fileUpload';

import { AcceptedFilesDescription } from './acceptedFilesDescription/acceptedFilesDescription.fileUpload';

import { FileType, HandleFileFn } from './types';
import { UploadFileFn } from '@/components/forms/utils/media';

import {
  ADD_FILES,
  REMOVE_FILE,
  ERROR,
  PROGRESS,
  SUCCESS
} from './constant/actions';
import { ERROR_TYPE_SIZE, ERROR_TYPE_EXTENSION } from './constant/errors';
import { reducer } from './reducer';

interface Props {
  formData: FileType[];
  onChange: HandleFileFn;
  required: boolean;
  schema: WidgetProps;
  formContext: {
    uploadFile: UploadFileFn;
    uploadConfig: {
      maxFileSize?: number;
      fileExtensionWhitelistMap?: Record<string, string[]>;
    };
    links: FormLinks;
  };
  idSchema: IdSchema;
  uiSchema: UiSchema<{
    'ui:options': {
      visibilitySwitchLabel?: string;
    };
  }>;
  name: string;
}

export const FileUploadField = memo((props: Props) => {
  const {
    schema: { title },
    formContext: { uploadFile, uploadConfig, links },
    formData,
    onChange,
    required,
    idSchema,
    uiSchema
  } = props;

  const [files, dispatch] = useReducer(reducer, formData || []);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!files) return;
    onChange(files);
  }, [files]);

  // check if the widget is locked because of forms links
  const fieldId = idSchema.$id;
  const isLocked = isLockedWidget(fieldId, links);

  const [dropzoneActive, setDropzoneActive] = useState(false);

  let dropzoneRef: any | undefined = useRef(null);

  /**
   * Count constants in order to display number of:
   * - files uploaded
   * - error files
   */
  const filesCount = files.length;
  const errorsCount = files.filter(f => f.error).length;

  // Set to fit within the batched forms

  const maxFileSize =
    (uploadConfig && uploadConfig.maxFileSize) || 1024 * 1024 * 10;

  // media-api should be feeding this map through the client (e.g: trace-ui)
  const fileExtensionWhitelistMap =
    (uploadConfig && uploadConfig.fileExtensionWhitelistMap) || null;

  /**
   * @function handleNewFiles - handles the files that are dropped
   * or selected using react-dropzone
   * @param {Array.<Object>} acceptedFiles
   */
  const onDropNewFiles: DropFilesEventHandler = useCallback(
    acceptedFiles => {
      if (isLocked) return;

      const fileExtensionWhitelist = fileExtensionWhitelistMap
        ? Object.values(fileExtensionWhitelistMap).flat(1)
        : [];

      const newFiles = acceptedFiles.filter(
        f => !files.find(i => f.name === i.name)
      );

      onDragLeave();
      if (!newFiles.length) return;

      const forbiddenFileExtensions = acceptedFiles.filter(
        file =>
          fileExtensionWhitelist.length > 0 &&
          !fileExtensionWhitelist.includes(getFileExtension(file.name) || '')
      );

      const exceedSizeLimit = acceptedFiles.filter(
        file =>
          file.size > maxFileSize && !forbiddenFileExtensions.includes(file)
      );

      const readyToUpload = acceptedFiles.filter(
        file =>
          !exceedSizeLimit.includes(file) &&
          !forbiddenFileExtensions.includes(file)
      );
      /**
       * @function onFileSuccess - reports a file with no error and set uploading to false
       * @param id
       */
      const onFileSuccess = (id: string) => (file: FileType) =>
        dispatch({ type: SUCCESS, payload: { id, file } });

      /**
       * @function onFileError - reports media-api error.
       * @param id
       * Fallback if the UI is unable to catch the error.
       * e.g: the file size after encryption exceeds media-api's size limit
       */
      const onFileError = (id: string) => () =>
        dispatch({ type: ERROR, payload: { id } });

      /**
       * @function onFileProgress - reports the percentage of the uploading file
       * @param id
       * @param percentage
       */
      const onFileProgress = (id: string, percentage: number) =>
        dispatch({
          type: PROGRESS,
          payload: { id, percentage }
        });

      const uploading = readyToUpload.map(file => {
        const id = `valid-${shortid.generate()}`;
        uploadFile(file, onFileSuccess(id), onFileError(id), progress =>
          onFileProgress(id, progress.uploadPercent)
        );
        return {
          id,
          name: file.name,
          size: file.size,
          uploading: true,
          uploadPercent: 0
        };
      });

      const errorsExceedSizeLimit = exceedSizeLimit.map(file => ({
        id: `err-too-big-${shortid.generate()}`,
        name: file.name,
        size: file.size,
        error: true,
        errorType: ERROR_TYPE_SIZE,
        uploading: false
      }));

      const errorsForbiddenExtension = forbiddenFileExtensions.map(file => ({
        id: `err-wrong-ext-${shortid.generate()}`,
        name: file.name,
        size: file.size,
        error: true,
        errorType: ERROR_TYPE_EXTENSION,
        uploading: false
      }));

      const errors = [...errorsExceedSizeLimit, ...errorsForbiddenExtension];
      const mergedFiles = [...uploading, ...errors];

      dispatch({
        type: ADD_FILES,
        payload: { mergedFiles }
      });
    },
    [isLocked, uploadFile, fileExtensionWhitelistMap, maxFileSize, files]
  );

  const handleDeleteFile = useCallback(
    (id: string) => dispatch({ type: REMOVE_FILE, payload: { id } }),
    []
  );

  const onDragEnter = () => !isLocked && setDropzoneActive(true);

  const onDragLeave = () => setDropzoneActive(false);

  const handleFileButtonClick = () => {
    dropzoneRef.open();
  };

  const showSwitch = !!uiSchema['ui:options']?.visibilitySwitchLabel; // Replace with uiSchema.files.options.showVisibilitySwitch
  const switchLabel = uiSchema['ui:options']?.visibilitySwitchLabel; // Replace with uiSchema.files.options.visibilitySwitchLabel
  const [showUpload, setShowUpload] = useState(!showSwitch);

  const handleSwitchClick = () => {
    setShowUpload(prevState => !prevState);
  };

  return (
    <>
      {showSwitch && (
        <div className="flex items-center space-x-2">
          <Switch
            id={`${fieldId}-switch`}
            checked={showUpload}
            onCheckedChange={handleSwitchClick}
            data-cy={`file-${props.name}-switch`}
          />
          <Label htmlFor={`${fieldId}-switch`}>{switchLabel as string}</Label>
        </div>
      )}
      {showUpload && (
        <FieldTemplate id={fieldId} label={title} {...(props as any)}>
          <FieldLabel
            fieldName={props.name}
            filesCount={filesCount}
            errorsCount={errorsCount}
          />
          <div
            className={cn(
              'border-input bg-card @container/FileUpload relative rounded-md border border-dashed transition-all duration-200',
              dropzoneActive && 'border-primary'
            )}
          >
            <Dropzone
              className={cn(
                'flex w-full flex-col gap-4 p-4 transition-all duration-200',
                dropzoneActive && 'opacity-20'
              )}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDrop={onDropNewFiles}
              // @ts-expect-error - TS is not happy with the data-cy prop
              inputProps={{ required, 'data-cy': `file-${props.name}-input` }}
              multiple
              disableClick
              data-cy={`file-${props.name}`}
              ref={e => {
                dropzoneRef = e;
              }}
            >
              {!isLocked && (
                <section className="my-4 flex w-full flex-row gap-1">
                  <div
                    className="relative z-2 flex grow flex-col items-center justify-center"
                    data-cy="forms-js-dropzone-fileupload"
                  >
                    <div className="text-muted-foreground mb-5">
                      <CloudUpload size={40} />
                    </div>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleFileButtonClick}
                      data-cy={`file-${props.name}-open-button`}
                      aria-label="Click to select files"
                    >
                      Select files
                    </Button>
                    {maxFileSize && (
                      <span className="text-muted-foreground py-2.5 text-center text-[10px] select-none">
                        The maximum file size is {filesize(maxFileSize)}
                      </span>
                    )}
                  </div>
                  <div className="hidden @sm/FileUpload:block @lg:w-1/3">
                    <AcceptedFilesDescription
                      fileExtensionWhitelistMap={fileExtensionWhitelistMap}
                      filesLength={filesCount}
                    />
                  </div>
                </section>
              )}
              {files?.length > 0 && (
                <section className="flex w-full flex-col gap-2">
                  {files?.map(file => (
                    <File
                      key={file.id}
                      file={file}
                      handleDeleteFile={handleDeleteFile}
                      maxFileSize={maxFileSize}
                      disabled={isLocked}
                      fieldName={props.name}
                    />
                  ))}
                </section>
              )}
            </Dropzone>
          </div>
        </FieldTemplate>
      )}
    </>
  );
});
